import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import { Box, HStack, Icon, Popover, Portal, Spinner, StackSeparator, useDisclosure } from '@chakra-ui/react'
import { FocusItemIcon, ResetIcon } from '@editor/assets/icons'
import { EDITOR_TOOLS, EDITOR_TOOL_BUTTON_SIZE } from '@editor/config/constants'
import { useEditorContext } from '@editor/contexts/Editor'
import { useGlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { hashString } from '@editor/hooks/useHashUpdater'
import { patchInspectionItems } from '@editor/pages/projects/editor/store/thunks/inspectionItems'
import ToolbarButton from '@editor/pages/projects/editor/toolbar/components/ToolbarButton'
import { COMMON_MESSAGE } from '@editor/services/ErrorHandler'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import * as Sentry from '@sentry/react'
import { BsGearFill } from 'react-icons/bs'

import { CameraProfileTarget } from '../definitions'
import { setProfileTarget } from '../store'
import CameraViewButton from './CameraViewButton'
import EditProfileIcon from './EditProfileIcon'

const FocusItemButton = () => {
  // Popover toolbar
  const { open, onOpen, onClose } = useDisclosure()

  // Context
  const { getAccessToken } = useContext(UserContext)
  const { changeTool } = useEditorContext()
  const { showErrorModal } = useGlobalModalContext()

  // Store
  const dispatch = useAppDispatch()
  const userType = useAppSelector((state) => state.user.userType)
  const project = useAppSelector((state) => state.page.project)
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)
  const inspectionSheet = useAppSelector((state) => state.inspectionItems.inspectionSheet)
  const selectedTool = useAppSelector((state) => state.editor.selectedTool)
  const permissionSet = useAppSelector((state) => state.editor.permissionSet)
  const focusedItem = useAppSelector((state) =>
    state.inspectionItems.items.find((item) => item.inspection_item_id === state.toolUtil.core.focusedElementId),
  )
  const isAllowedToModify = permissionSet.MODIFY.includes(userType)
  const isEditingItemProfile = useAppSelector(
    (state) =>
      state.toolCameraProfile.profileTarget === CameraProfileTarget.Item &&
      state.editor.selectedTool === EDITOR_TOOLS.CAMERA_PROFILE,
  )

  // States
  const [isResettingProfile, setIsResettingProfile] = useState(false)

  // Refs
  const cameraProfileButtonRef = useRef<HTMLDivElement>(null)

  /**
   * Switch to edit mode when the camera profile button is clicked in the sub-toolbar.
   */
  const switchEditMode = useCallback(
    (tool: string) => {
      dispatch(setProfileTarget(CameraProfileTarget.Item))
      changeTool(tool)
    },
    [changeTool, dispatch],
  )

  /**
   * Reset an item's camera profile by removing it from its inspection item.
   */
  const handleResetCustomProfile = useCallback(async () => {
    if (
      isResettingProfile ||
      !focusedItem?.camera_profile ||
      !project?.project_id ||
      !inspectionArea?.inspection_area_id ||
      !inspectionSheet?.inspection_sheet_id
    )
      return

    try {
      setIsResettingProfile(true)

      const accessToken = await getAccessToken()
      if (!accessToken) return

      await dispatch(
        patchInspectionItems({
          accessToken,
          projectId: project.project_id,
          items: [
            {
              inspection_item_id: focusedItem.inspection_item_id!,
              inspection_area_id: inspectionArea.inspection_area_id,
              inspection_sheet_id: inspectionSheet.inspection_sheet_id,
              camera_profile: null,
            },
          ],
        }),
      ).unwrap()
    } catch (error) {
      Sentry.captureException(error)
      showErrorModal(COMMON_MESSAGE)
    } finally {
      setIsResettingProfile(false)
    }
  }, [
    isResettingProfile,
    project?.project_id,
    inspectionArea?.inspection_area_id,
    inspectionSheet?.inspection_sheet_id,
    focusedItem,
    getAccessToken,
    showErrorModal,
    dispatch,
  ])

  /**
   * Close sub-toolbar when the selected tool is camera profile.
   */
  useEffect(() => {
    if (isEditingItemProfile) {
      onClose()
    }
  }, [isEditingItemProfile, onClose])

  if (!focusedItem) return null

  return (
    <Box w={EDITOR_TOOL_BUTTON_SIZE} h={EDITOR_TOOL_BUTTON_SIZE} position="relative">
      <CameraViewButton
        ref={cameraProfileButtonRef}
        label="Focus on item"
        icon={<FocusItemIcon size="1.2em" />}
        superIcon={
          isEditingItemProfile ? (
            <Box position="absolute" top="7px" right="7px" width="100%" height="100%">
              <Icon
                as={BsGearFill}
                position="absolute"
                top="-2px"
                right="-2px"
                width="75%"
                height="75%"
                color="black"
              />
              <Icon
                as={BsGearFill}
                position="absolute"
                top="-2px"
                right="-2px"
                width="65%"
                height="65%"
                color={isEditingItemProfile ? 'orange' : 'white'}
              />
            </Box>
          ) : undefined
        }
        onClick={() => {
          document.dispatchEvent(
            new CustomEvent('editor:inspection-item:focus', {
              detail: hashString(focusedItem.inspection_item_id!),
            }),
          )
        }}
        onLongPress={onOpen}
        hasSubtoolbar
        isActive={isEditingItemProfile}
      />

      <Box position="relative">
        <Popover.Root
          open={open}
          onOpenChange={(e) => (e.open ? onOpen() : onClose())}
          closeOnInteractOutside
          closeOnEscape
          positioning={{
            placement: 'top',
            getAnchorRect() {
              return cameraProfileButtonRef.current?.getBoundingClientRect() || null
            },
          }}
        >
          <Portal>
            <Popover.Positioner>
              <Popover.Content w="fit-content" borderWidth={0} backgroundColor="transparent">
                <Popover.Body
                  p={0}
                  backgroundColor="secondary.800"
                  borderColor="secondary.700"
                  borderWidth={1}
                  borderRadius="md"
                >
                  <HStack
                    h={EDITOR_TOOL_BUTTON_SIZE}
                    backgroundColor="gray2.800"
                    borderRadius="md"
                    separator={<StackSeparator borderColor="whiteAlpha.200" />}
                    gap={0}
                    data-testid="camera-profile-view-orientation-bar"
                  >
                    {isAllowedToModify && (
                      <>
                        <ToolbarButton
                          icon={<EditProfileIcon />}
                          label="正面を設定"
                          toolType={EDITOR_TOOLS.CAMERA_PROFILE}
                          buttonSize={EDITOR_TOOL_BUTTON_SIZE}
                          selectedTool={selectedTool}
                          changeTool={switchEditMode}
                          toolTipPlacement="top"
                        />
                        {focusedItem?.camera_profile && (
                          <CameraViewButton
                            label="デフォルトに戻す"
                            icon={isResettingProfile ? <Spinner size="sm" /> : <ResetIcon />}
                            disabled={isResettingProfile}
                            onClick={handleResetCustomProfile}
                          />
                        )}
                      </>
                    )}
                  </HStack>
                </Popover.Body>
              </Popover.Content>
            </Popover.Positioner>
          </Portal>
        </Popover.Root>
      </Box>
    </Box>
  )
}

export default FocusItemButton
